"use client";

import { useRef, useState, useEffect } from "react";
import { useLocale } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";
import { Globe } from "lucide-react";
import { cn } from "@/lib/utils";

const LOCALE_NAMES: Record<string, { short: string; full: string }> = {
  en: { short: "EN", full: "English" },
  es: { short: "ES", full: "Español" },
};

type Locale = (typeof routing.locales)[number];

export function LanguageSelector() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-muted hover:text-text rounded-lg transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe className="h-[15px] w-[15px]" />
        <span>{LOCALE_NAMES[locale]?.short ?? locale.toUpperCase()}</span>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full mt-1.5 w-32 bg-surface border border-border rounded-lg shadow-xl overflow-hidden z-50"
        >
          {(routing.locales as readonly string[]).map((loc) => (
            <button
              key={loc}
              role="option"
              aria-selected={loc === locale}
              onClick={() => {
                router.replace(pathname, { locale: loc as Locale });
                setOpen(false);
              }}
              className={cn(
                "w-full px-3 py-2.5 text-sm text-left transition-colors hover:bg-surface-2",
                loc === locale ? "text-accent font-semibold" : "text-text"
              )}
            >
              {LOCALE_NAMES[loc]?.full ?? loc}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
